// story: каталог сюжетного режима «Книга заклинаний» — видео-слоты существ,
// размеры билбордов, оттенки, звуки и запасные тексты этапов.
//
// Видео-слоты: client/public/textures/story/<слот>.webm (VP9 с альфой) +
// <слот>.mov (HEVC с альфой, для Safari) с одинаковым именем. Клипов пока
// нет (их сгенерирует Kling) — пока файла нет, слот берёт свой fallback-слот,
// затем заглушку STUB_VIDEO (zombi.webm), затем рисованный силуэт. Требования
// к клипам — client/public/textures/story/README.md.

import { StoryKind, StoryState } from "../net/protocol";

export const STORY_VIDEO_DIR = "/textures/story/";
/** Заглушка для всех слотов, пока нет своих клипов. */
export const STUB_VIDEO = "/textures/levelpacks/alone_in_the_dark/zombi.webm";

export interface VideoSlotDef {
  /** Имя файла без расширения. */
  name: string;
  /** true — петля (общий плеер на всех); false — один раз (свой плеер у существа). */
  loop: boolean;
  /** Ожидаемая длительность клипа, с (для README и синхронизации one-shot). */
  seconds: number;
  /** Слот, который подставляется, если файла нет (раньше заглушки). */
  fallback?: string;
  /** Не подставлять zombi.webm — сразу рисованный вариант (книга). */
  noStub?: boolean;
  /** Смерть: после клипа тело лежит (последний кадр держится, пока тает). */
  holdLast?: boolean;
  /** Что в кадре — для README и для рисованной заглушки. */
  note: string;
  /** Ходьба: сколько метров проходят ноги за один проход петли. Клиент
   *  ускоряет/замедляет ролик под скорость существа — иначе плоскость едет,
   *  а ноги шагают в своём темпе, и зомби будто летит (21.10). */
  metersPerLoop?: number;
}

export const VIDEO_SLOTS: Record<string, VideoSlotDef> = {
  // Ролик 2,73 с: два шаркающих шага (замер по ступням) — считаем по 0,8 м.
  zombie_walk:     { name: "zombie_walk", loop: true, seconds: 2.0, note: "зомби ковыляет на камеру", metersPerLoop: 1.6 },
  zombie_idle:     { name: "zombie_idle", loop: true, seconds: 3.0, fallback: "zombie_walk", note: "(необязательный) зомби покачивается на месте" },
  zombie_attack:   { name: "zombie_attack", loop: false, seconds: 1.4, fallback: "zombie_walk", note: "замах и удар: удар в 0,5 с" },
  zombie_die:      { name: "zombie_die", loop: false, seconds: 1.7, fallback: "zombie_walk", holdLast: true, note: "падает; последний кадр — лежит" },
  ghost_girl_bike: { name: "ghost_girl_bike", loop: true, seconds: 2.0, note: "девочка на велосипеде в профиль, едет ВПРАВО" },
  ghost_woman:     { name: "ghost_woman", loop: true, seconds: 4.0, note: "плачущая женщина в ночной сорочке бредёт на камеру" },
  // Ролик 2,5 с: два шага по 0,6 м.
  hag_walk:        { name: "hag_walk", loop: true, seconds: 2.5, note: "старуха с топором идёт на камеру", metersPerLoop: 1.2 },
  hag_idle:        { name: "hag_idle", loop: true, seconds: 3.0, fallback: "hag_walk", note: "(необязательный) старуха стоит, точит топор" },
  hag_attack:      { name: "hag_attack", loop: false, seconds: 1.7, fallback: "hag_walk", note: "замах топором: удар в 0,8 с" },
  hag_die:         { name: "hag_die", loop: false, seconds: 2.5, fallback: "hag_walk", holdLast: true, note: "падает, рассыпается; последний кадр — лежит" },
  painting_1:      { name: "painting_1", loop: true, seconds: 5.0, note: "портрет: дама в чёрном (оживает)" },
  painting_2:      { name: "painting_2", loop: true, seconds: 5.0, note: "портрет: старик со свечой (оживает)" },
  painting_3:      { name: "painting_3", loop: true, seconds: 5.0, note: "портрет: бледный мальчик (оживает)" },
  book:            { name: "book", loop: true, seconds: 3.0, noStub: true, note: "(необязательный) парящая книга, страницы листаются" },
};

/** Слот для существа в состоянии (null — не рисовать). */
export function slotFor(kind: number, state: number, variant: number): string | null {
  switch (kind) {
    case StoryKind.Zombie:
      if (state === StoryState.Attack) return "zombie_attack";
      if (state === StoryState.Dying || state === StoryState.Dead) return "zombie_die";
      if (state === StoryState.Idle) return "zombie_idle";
      return "zombie_walk";
    case StoryKind.Hag:
      if (state === StoryState.Attack) return "hag_attack";
      if (state === StoryState.Dying || state === StoryState.Dead) return "hag_die";
      if (state === StoryState.Idle) return "hag_idle";
      return "hag_walk";
    case StoryKind.GhostGirl:
      return "ghost_girl_bike";
    case StoryKind.GhostWoman:
      return "ghost_woman";
    case StoryKind.Painting:
      return `painting_${Math.min(3, Math.max(1, variant || 1))}`;
    case StoryKind.Book:
      return "book";
  }
  return null;
}

/** Оформление билборда по виду существа. */
export interface KindLook {
  /** Сторона квадратного квада, м (клип 720×720). */
  size: number;
  /** Доля кадра под ступнями (прозрачный низ клипа). */
  footPad: number;
  /** Подъём над полом, м (книга парит). */
  hover: number;
  /** Режим шейдера: 0 освещён, 1 призрак (светится сам), 2 картина, 3 книга. */
  mode: number;
  tint: [number, number, number];
  alpha: number;
  emissive: number;
  /** Пятно тени под ногами. */
  shadow: number;
  /** Цвет кромки растворения/таяния. */
  edge: [number, number, number];
  /** Зеркалить клип по направлению движения (клип снят «едет вправо»). */
  mirrorByMotion: boolean;
}

export const KIND_LOOK: Record<number, KindLook> = {
  [StoryKind.Zombie]: {
    size: 2.15, footPad: 0.04, hover: 0, mode: 0, tint: [1, 1, 1], alpha: 1, emissive: 0,
    shadow: 0.42, edge: [1.0, 0.35, 0.08], mirrorByMotion: false,
  },
  [StoryKind.Hag]: {
    size: 2.2, footPad: 0.04, hover: 0, mode: 0, tint: [1.0, 0.94, 0.88], alpha: 1, emissive: 0,
    shadow: 0.48, edge: [1.0, 0.35, 0.08], mirrorByMotion: false,
  },
  [StoryKind.GhostGirl]: {
    size: 1.95, footPad: 0.04, hover: 0.02, mode: 1, tint: [0.72, 0.9, 1.15], alpha: 0.78, emissive: 0.35,
    shadow: 0, edge: [0.55, 0.95, 1.0], mirrorByMotion: true,
  },
  [StoryKind.GhostWoman]: {
    size: 2.05, footPad: 0.04, hover: 0.08, mode: 1, tint: [0.78, 0.92, 1.12], alpha: 0.72, emissive: 0.3,
    shadow: 0, edge: [0.55, 0.95, 1.0], mirrorByMotion: false,
  },
  [StoryKind.Painting]: {
    size: 1.15, footPad: 0, hover: 0, mode: 2, tint: [1, 1, 1], alpha: 1, emissive: 0,
    shadow: 0, edge: [0.9, 0.7, 0.3], mirrorByMotion: false,
  },
  [StoryKind.Book]: {
    size: 0.75, footPad: 0, hover: 0.5, mode: 3, tint: [1, 1, 1], alpha: 1, emissive: 0.6,
    shadow: 0.25, edge: [0.8, 0.4, 1.0], mirrorByMotion: false,
  },
};

/** Длительности, которые задаёт сервер (story_mobs.go) — для эффектов. */
export const DISSOLVE_SEC = 0.8;
export const CORPSE_SEC = 2.5;
/** Сколько тело просто лежит, прежде чем начать уходить в пол и таять (с). */
export const CORPSE_HOLD_SEC = 0.7;
/** На сколько тело уходит в пол к концу таяния (м). Лежащий зомби — ~0,3 м
 *  кадра: глубже — и он прячется в пол раньше, чем успевает растаять. */
export const CORPSE_SINK = 0.14;

/** Звуки: свой файл в /sounds/story/<имя>.wav, пока его нет — заглушка. */
export const STORY_SOUNDS: Record<string, { file: string; stub: string; rate: number; vol: number }> = {
  zombie_hit:   { file: "/sounds/story/zombie_hit.wav", stub: "/sounds/weapons/punch.wav", rate: 0.7, vol: 0.9 },
  hag_hit:      { file: "/sounds/story/hag_hit.wav", stub: "/sounds/weapons/swing.wav", rate: 0.6, vol: 1.0 },
  mob_die:      { file: "/sounds/story/mob_die.wav", stub: "/sounds/weapons/bounce.wav", rate: 0.5, vol: 0.8 },
  ghost_vanish: { file: "/sounds/story/ghost_vanish.wav", stub: "/sounds/artifacts/spawn.wav", rate: 0.55, vol: 0.8 },
  scare:        { file: "/sounds/story/scare.wav", stub: "/sounds/artifacts/spawn.wav", rate: 0.35, vol: 1.0 },
  whisper:      { file: "/sounds/story/whisper.wav", stub: "/sounds/artifacts/pickup.wav", rate: 0.45, vol: 0.5 },
  book:         { file: "/sounds/story/book.wav", stub: "/sounds/artifacts/relic.wav", rate: 0.8, vol: 1.0 },
  stage:        { file: "/sounds/story/stage.wav", stub: "/sounds/artifacts/relic.wav", rate: 0.5, vol: 0.6 },
};

/** Запасные тексты этапов (если вошли посреди забега и не слышали события). */
export const STAGE_TEXT: Record<number, { title: string; hint: string }> = {
  1: { title: "Проберитесь в особняк", hint: "Через сад к дому на холме. Двери — клавиша E" },
  2: { title: "Найдите книгу заклинаний", hint: "Картины шепчут тому, кто на них смотрит" },
  3: { title: "Победите старуху", hint: "Она медленная, но топор бьёт насмерть" },
  4: { title: "Возьмите книгу", hint: "Книга парит в дальнем конце чердака" },
  5: { title: "Книга ваша", hint: "" },
};

export const STORY_TITLE = "Книга заклинаний";
